import type { HomepageLayoutMode } from '@/lib/content-types';

/** Frontpage `key` of the main editorial homepage section ("דף הבית"). */
export const FRONTPAGE_HOME = 'fp-16387';
/** Frontpage `key` of the secondary "more in Ashkelon" rail ("עוד באשקלון"). */
export const FRONTPAGE_MORE = 'fp-16386';

/** Default homepage layout when the home frontpage row has no layout set. */
export const DEFAULT_HOMEPAGE_LAYOUT: HomepageLayoutMode = 'columns';

export function isHomepageLayoutMode(value: unknown): value is HomepageLayoutMode {
    return value === 'columns' || value === 'hero-full';
}

export function resolveHomepageLayout(value: unknown): HomepageLayoutMode {
    return isHomepageLayoutMode(value) ? value : DEFAULT_HOMEPAGE_LAYOUT;
}

export function frontpageNavLabel(row: {
    key: string;
    title: string | null;
}): string {
    const title = row.title?.trim();
    if (title) return title;
    if (row.key === FRONTPAGE_HOME) return 'דף הבית';
    if (row.key === FRONTPAGE_MORE) return 'עוד באשקלון';
    return row.key;
}

/** Home block first, then "more", then remaining keys. */
export function sortFrontpageNavRows<T extends { key: string }>(rows: T[]): T[] {
    const rank = (key: string) => {
        if (key === FRONTPAGE_HOME) return 0;
        if (key === FRONTPAGE_MORE) return 1;
        return 2;
    };
    return [...rows].sort((a, b) => {
        const byRank = rank(a.key) - rank(b.key);
        if (byRank !== 0) return byRank;
        return a.key.localeCompare(b.key);
    });
}

/**
 * Place `articleId` at a 1-based slot in an ordered frontpage id list.
 * Existing occurrence is removed first; position is clamped to the list bounds.
 * Returns a new array (does not mutate `articleIds`).
 */
export function placeArticleAtPosition(
    articleIds: string[],
    articleId: string,
    position: number,
): string[] {
    const next = articleIds.filter((id) => id !== articleId);
    if (position < 1) return next;
    const index = Math.min(Math.max(position, 1), next.length + 1) - 1;
    next.splice(index, 0, articleId);
    return next;
}
